import * as THREE from "three";

// Distant mountain-silhouette rings that hide the edge of the playable world.
// Two overlapping ridgelines (near: taller and darker, far: lower and hazier)
// sit out on the far-field disc; scene fog dissolves their bases into the
// sky's horizon band. Pure silhouette dressing — excluded from ballistics,
// line of sight, and navigation, and unlit (vertex-colored basic material)
// so it always reads as atmosphere rather than geometry.

export type HorizonPalette = {
  ridge: number; // near-ridge tone (darker than horizon sky)
  haze: number; // tone the ridges dissolve into — match fog/sky horizon
};

type Rng = { next: () => number };

function seededRng(seed: number): Rng {
  let s = seed | 0;
  return {
    next(): number {
      s = Math.imul(s, 1664525) + 1013904223 | 0;
      return (s >>> 0) / 0xffffffff;
    },
  };
}

// Periodic ridge profile: a small sum of sinusoids with random phases wraps
// seamlessly around the circle and reads as a natural skyline.
function ridgeProfile(rng: Rng): (angle: number) => number {
  const waves = [3, 5, 8, 13, 21].map((freq) => ({
    freq,
    amp: (0.5 + rng.next() * 0.5) / Math.sqrt(freq),
    phase: rng.next() * Math.PI * 2,
  }));
  return (angle: number): number => {
    let h = 0;
    for (const w of waves) h += Math.sin(angle * w.freq + w.phase) * w.amp;
    return h * 0.5 + 0.5; // roughly 0..1
  };
}

function buildRidgeRing(
  radius: number,
  peakHeight: number,
  color: THREE.Color,
  hazeColor: THREE.Color,
  hazeAmount: number,
  rng: Rng,
): THREE.Mesh {
  const segments = 256;
  const profile = ridgeProfile(rng);
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  const topColor = color.clone().lerp(hazeColor, hazeAmount);
  const baseColor = hazeColor;

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const wobble = 1 + (profile(angle * 2 + 1.7) - 0.5) * 0.08; // radial irregularity
    const x = Math.cos(angle) * radius * wobble;
    const z = Math.sin(angle) * radius * wobble;
    const height = Math.max(0.12, profile(angle)) * peakHeight;

    positions.push(x, -40, z); // base sits below the far-field surface
    positions.push(x, height, z);
    colors.push(baseColor.r, baseColor.g, baseColor.b);
    // Taller peaks stay slightly crisper than saddles
    const peakBlend = THREE.MathUtils.clamp(height / peakHeight, 0, 1);
    const c = baseColor.clone().lerp(topColor, 0.35 + peakBlend * 0.65);
    colors.push(c.r, c.g, c.b);

    if (i < segments) {
      const base = i * 2;
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geo.setIndex(indices);

  const mat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    fog: true,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData["ignoreBallistics"] = true;
  mesh.userData["losTransparent"] = true;
  return mesh;
}

export function addHorizonRing(
  parent: THREE.Group,
  mapSize: number,
  palette: HorizonPalette,
  seed = 0x0a11ce,
): void {
  const rng = seededRng(seed);
  const ridge = new THREE.Color(palette.ridge);
  const haze = new THREE.Color(palette.haze);

  // Far ring first (renders behind): lower, hazier, further out. Heights
  // sized to back the amplified in-map peaks rather than duck under them.
  parent.add(buildRidgeRing(mapSize * 1.38, mapSize * 0.15, ridge, haze, 0.72, rng));
  // Near ring: taller, a touch darker, still fog-shrouded at its base
  parent.add(buildRidgeRing(mapSize * 1.12, mapSize * 0.095, ridge, haze, 0.5, rng));
}
